import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { InvoiceHeader } from "./InvoiceHeader";
import { InvoiceInformation } from "./InvoiceInformation";
import { InvoiceContent } from "./InvoiceContent";
import {
  InvoiceEdit,
  InvoiceRow,
  InvoiceSavePost,
  ManualInvoicePost,
  TYPE_EXTERNAL,
} from "../invoiceAPI";
import {
  fetchCustomerOrgContracts,
  createManualInvoice,
  updateInvoice,
  deleteInvoice,
  deleteInvoicesBundle,
  refreshInvoiceData,
} from "../invoiceDetailedSlice";
import { sendInvoices } from "../invoiceSlice";
import useMessage from "../../../hooks/useMessage";
import { EditInvoiceActionBanner } from "./EditInvoiceActionBanner";
import { CustomerContract } from "../../customer/customerAPI";
import { InvoiceContainer } from "../styled";
import { InvoiceAttachments } from "../attachments/InvoiceAttachments";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { INVOICE_EDITED, INVOICE_SENT_SUCCESS } from "../constants";
import { canExpertModifyInvoices } from "../../user/accessRightChecks";
import useInvoicePaging from "../hooks/useInvoicePaging";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";
import isEqual from "../../../common/helpers/isEqual";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import { format } from "date-fns";
import { validateDate } from "../../../app/common";
import { toTwoDecimalString } from "../utils";

interface Props {
  existingInvoices: InvoiceEdit[]; // Single invoice or bundle of credit invoices
  isCreditInvoiceBundle?: boolean;
}

export const EditInvoiceComponent = ({
  existingInvoices,
  isCreditInvoiceBundle,
}: Props) => {
  const navigate = useNavigate();
  const { sendMessage } = useMessage();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.user);
  const { status } = useAppSelector((state) => state.invoicesDetailed);
  const [changingInvoice, setChangingInvoice] = useState<boolean>(false);
  const isLoading = status === "pending" || changingInvoice;

  const [customerContracts, setCustomerContracts] = useState<
    CustomerContract[]
  >([]);

  const [uploadFile, setUploadFile] = useState<File>();

  const [isInfoValid, setIsInfoValid] = useState<boolean>(false);
  const [areRowContentsValid, setAreRowContentsValid] =
    useState<boolean>(false);
  const [areRowsNotEmpty, setAreRowsNotEmpty] = useState<boolean>(false);

  const [isSaveAllowed, setIsSaveAllowed] = useState<boolean>(false);
  const [isDeleteAllowed, setIsDeleteAllowed] = useState<boolean>(false);
  const [isUserAllowedToEdit, setIsUserAllowedToEdit] = useState(false);

  const resetState = () => {
    // Reset invoice state
    setCustomerContracts([]);
    setUploadFile(undefined);
    setIsInfoValid(false);
    setAreRowContentsValid(false);
    setAreRowsNotEmpty(false);
    // Reset button states
    setIsSaveAllowed(false);
    setIsDeleteAllowed(false);
  };

  const onChangeInvoice = () => {
    setChangingInvoice(true);
    resetState();
  };

  const { selectedIndex, cycleInvoices } = useInvoicePaging({
    invoicesLength: existingInvoices.length,
    onCycle: onChangeInvoice,
  });
  const selectedInvoice = useMemo(
    () => existingInvoices[selectedIndex],
    [existingInvoices, selectedIndex],
  );
  const [invoice, setInvoice] = useState<InvoiceEdit>(selectedInvoice);
  const isNewInvoice = !invoice.invoiceNumber;

  const [formIsDirty, setFormIsDirty] = useState<boolean>(false);

  useEffect(() => {
    // When creating a new invoice, selectedInvoice is removed from the store after creation.
    // Check whether selectedInvoice exists to prevent unsaved changes trigger on 'save and send'.
    setFormIsDirty(
      !!selectedInvoice ? !isEqual(invoice, selectedInvoice) : false,
    );
  }, [invoice, selectedInvoice]);

  // https://react.dev/reference/react/useCallback#updating-state-from-a-memoized-callback
  const updateInfo = useCallback((newInvoiceData: any, isValid: boolean) => {
    setInvoice((prevInvoiceData) => ({
      ...prevInvoiceData,
      ...newInvoiceData,
    }));
    setIsInfoValid(isValid);
  }, []);

  // https://react.dev/reference/react/useCallback#updating-state-from-a-memoized-callback
  const updateInvoiceRows = useCallback(
    (newRows: InvoiceRow[], areRowsValid: boolean) => {
      setInvoice((prevInvoiceData) => ({
        ...prevInvoiceData,
        invoiceRows: newRows,
      }));
      setAreRowContentsValid(areRowsValid);
      setAreRowsNotEmpty(newRows.length > 0);
    },
    [],
  );

  const addAttachment = (newAttachment: File) => {
    setUploadFile(newAttachment);
  };

  const clearUpload = useCallback(() => {
    setUploadFile(undefined);
  }, []);

  const handleCreateInvoice = (sendInvoice: boolean) => {
    setFormIsDirty(false);
    const formattedDeliveryDate =
      invoice.deliveryDate && validateDate(invoice.deliveryDate, isoFormat)
        ? format(invoice.deliveryDate, isoFormat)
        : null;
    let manualInvoice: ManualInvoicePost = {
      invoiceType: invoice.invoiceType,
      invoiceDate: invoice.invoiceDate,
      invoiceValueDate: invoice.invoiceValueDate,
      deliveryDate: formattedDeliveryDate,
      eezyCompanyId: invoice.eezyCompanyId,
      paymentTermDays: invoice.paymentTermDays.toString(),
      sellerReferenceIdentifier: invoice.sellerReferenceIdentifier,
      buyerReferenceIdentifier: invoice.buyerReferenceIdentifier,
      orderIdentifier: invoice.orderIdentifier,
      agreementIdentifier: invoice.agreementIdentifier,
      ...(invoice.originalInvoiceNumber && {
        originalInvoiceNumber: invoice.originalInvoiceNumber,
      }),
      salesPersonId: invoice.salesPersonId,
      buyerContactPersonId: invoice.buyerContactPersonId,
      invoiceRows: invoice.invoiceRows.map((row) => ({
        ...row,
        quantityAmount: toTwoDecimalString(row.quantityAmount),
        unitPriceAmount: toTwoDecimalString(row.unitPriceAmount),
        vatRatePercent: toTwoDecimalString(row.vatRatePercent),
      })),
    };
    if (invoice.invoiceType === TYPE_EXTERNAL) {
      manualInvoice = {
        ...manualInvoice,
        customerOrganizationId: invoice.customerOrganizationId,
        contractId: invoice.contractId,
      };
    } else {
      manualInvoice = {
        ...manualInvoice,
        eezyBuyerCompanyId: invoice.eezyBuyerCompanyId,
      };
    }
    dispatch(createManualInvoice(manualInvoice)).then((action) => {
      // If invoice was successfully created and it needs to be sent, send it
      if (isFulfilled(action)) {
        if (sendInvoice) {
          const invoiceNumber = action.payload.find(
            (event) => event.name === "invNew",
          )?.payload.invoiceNumber;
          const id = action.payload.find(
            (event) => event.name === "invNew",
          )?.id!!;
          setInvoice({ ...invoice, invoiceNumber, id });

          dispatch(sendInvoices([id])).then(() => navigate("/home/invoices"));
        } else {
          sendMessage(t("invoiceSaved", { ns: "invoices" }), "success");
          navigate("/home/invoices");
        }
      } else if (isRejected(action)) {
        setFormIsDirty(true);
      }
    });
  };

  const handleUpdateInvoice = (sendInvoice: boolean) => {
    const invoiceRowsFromStore = selectedInvoice.invoiceRows;
    const formattedDeliveryDate = invoice.deliveryDate
      ? format(invoice.deliveryDate, isoFormat)
      : null;
    const updatedInvoice: InvoiceSavePost = {
      invoiceId: invoice.id!!,
      invoiceDate: invoice.invoiceDate,
      invoiceValueDate: invoice.invoiceValueDate,
      deliveryDate: formattedDeliveryDate,
      paymentTermDays: invoice.paymentTermDays,
      sellerReferenceIdentifier: invoice.sellerReferenceIdentifier || "",
      buyerReferenceIdentifier: invoice.buyerReferenceIdentifier || "",
      orderIdentifier: invoice.orderIdentifier || "",
      agreementIdentifier: invoice.agreementIdentifier || "",
      salesPersonId: invoice.salesPersonId,
      buyerContactPersonId: invoice.buyerContactPersonId,
      buyerContactPersonName: invoice.buyerContactPersonName,
      version: invoice.version!!,
      invoiceRows: invoice.invoiceRows.map((row) => {
        let rowObject: InvoiceRow = {
          ...row,
          quantityAmount: toTwoDecimalString(row.quantityAmount),
          unitPriceAmount: toTwoDecimalString(row.unitPriceAmount),
          vatRatePercent: toTwoDecimalString(row.vatRatePercent),
        };
        // invoiceRowsFromStore can be undefined when Credit invoice is created and the new invoice doesn't have invoiceRows.
        if (!invoiceRowsFromStore) return rowObject;
        invoiceRowsFromStore.forEach((rowFromStore) => {
          if (row.id === rowFromStore.id)
            rowObject = { ...rowObject, version: rowFromStore.version };
        });
        return rowObject;
      }),
    };
    setFormIsDirty(false);
    dispatch(updateInvoice(updatedInvoice))
      .then((action) => {
        // If invoice was successfully updated and it needs to be sent, send it
        if (isFulfilled(action)) {
          if (sendInvoice) {
            dispatch(
              sendInvoices(
                isCreditInvoiceBundle
                  ? existingInvoices.map((invoice) => invoice.id!!)
                  : [invoice.id!!],
              ),
            ).then(() => {
              navigate("/home/invoices");
            });
          } else {
            sendMessage(t("invoiceSaved", { ns: "invoices" }), "success");
          }
        }
      })
      .catch((err) => sendMessage(t(err.message), "error"));
  };

  const handleSave = (sendInvoice: boolean) => {
    if (isNewInvoice) {
      handleCreateInvoice(sendInvoice);
    } else {
      handleUpdateInvoice(sendInvoice);
    }
  };

  const handleDelete = () => {
    setFormIsDirty(false);
    dispatch(
      isCreditInvoiceBundle
        ? deleteInvoicesBundle(invoice.bundleId!!)
        : deleteInvoice(invoice.id!!),
    ).then((action) => isFulfilled(action) && navigate("/home/invoices"));
  };

  const handleRefresh = () => {
    dispatch(refreshInvoiceData(invoice.id!!)).then((action) => {
      if (isFulfilled(action)) {
        sendMessage(t("invoices:invoiceDataRefreshed"), "success");
      }
    });
  };

  // When selected invoice changes, set new invoice data to state
  useEffect(() => {
    if (selectedInvoice) {
      const invoiceData: InvoiceEdit = {
        ...selectedInvoice,
        paymentTermDays: selectedInvoice.paymentTermDays || "0",
      };
      setInvoice(invoiceData);

      if (!!selectedInvoice.customerOrganizationId) {
        dispatch(
          fetchCustomerOrgContracts(selectedInvoice.customerOrganizationId),
        ).then((action) => {
          isFulfilled(action) &&
            setCustomerContracts(
              action.payload.filter(
                (contract) =>
                  contract.eezyCompany.id === selectedInvoice.eezyCompanyId,
              ),
            );
          setChangingInvoice(false);
        });
      } else {
        setChangingInvoice(false);
      }
    }
  }, [selectedInvoice, dispatch]);

  // Determine if saving is allowed
  useEffect(() => {
    if (
      isInfoValid &&
      areRowsNotEmpty &&
      areRowContentsValid &&
      invoice.invoiceStatus !== INVOICE_SENT_SUCCESS
    ) {
      setIsSaveAllowed(true);
    } else {
      setIsSaveAllowed(false);
    }
  }, [
    isInfoValid,
    areRowsNotEmpty,
    areRowContentsValid,
    invoice.invoiceStatus,
  ]);

  // Determine if deleting is allowed
  useEffect(() => {
    if (
      invoice.invoiceNumber &&
      !invoice.invoiceWave &&
      invoice.invoiceStatus !== INVOICE_SENT_SUCCESS
    ) {
      setIsDeleteAllowed(true);
    } else {
      setIsDeleteAllowed(false);
    }
  }, [invoice.invoiceNumber, invoice.invoiceWave, invoice.invoiceStatus]);

  // Check user permissions
  useEffect(() => {
    if (!user) return;
    setIsUserAllowedToEdit(canExpertModifyInvoices(user));
  }, [user]);

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      {!changingInvoice && (
        <InvoiceContainer id="invoice-container">
          <UnsavedChangesProvider isDirty={formIsDirty}>
            <InvoiceHeader
              invoiceNumber={invoice.invoiceNumber}
              customer={
                invoice.invoiceType === TYPE_EXTERNAL
                  ? invoice.customerOrganizationName
                  : invoice.eezyBuyerCompanyName
              }
              addAttachment={
                isUserAllowedToEdit && isSaveAllowed ? addAttachment : undefined
              }
            />
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "8px",
                marginTop: "24px",
                padding: "16px",
              }}
            >
              <InvoiceInformation
                invoice={invoice}
                customerContracts={customerContracts}
                updateInfo={updateInfo}
              />
              <InvoiceContent
                invoice={invoice}
                updateInvoiceRows={updateInvoiceRows}
              />
              <InvoiceAttachments
                uploadFile={uploadFile}
                clearUpload={clearUpload}
                invoiceId={invoice.id || ""}
                isDeleteAllowed={isUserAllowedToEdit && isSaveAllowed}
              />
            </Box>
          </UnsavedChangesProvider>
        </InvoiceContainer>
      )}
      {isUserAllowedToEdit ? (
        <EditInvoiceActionBanner
          translation="invoices"
          showActionButtons={!changingInvoice}
          isSaveAllowed={isSaveAllowed}
          canDelete={isDeleteAllowed}
          canRefresh={
            selectedInvoice?.invoiceStatus === INVOICE_EDITED &&
            !!selectedInvoice.invoiceWave
          }
          onSave={() => handleSave(false)}
          onAcceptAndSend={() => handleSave(true)}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          currentInvoice={selectedIndex + 1}
          numberOfInvoices={existingInvoices.length}
          cycleInvoices={cycleInvoices}
        />
      ) : null}
    </>
  );
};
